import { FC, useState } from "react";
import { Box, VStack, Text, Icon, Flex, Divider, Collapse } from "@chakra-ui/react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { IconHome, IconFileSearch, IconHistory, IconReportAnalytics, IconChevronDown, IconChevronRight, IconChartLine } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { getConfig } from "../../../api/estudo";
import { useAcesso } from "@Hooks/useAcesso";
import { RELATORIOS } from "@Pages/Relatorios";
import { PAINEIS } from "@Pages/Precos";
import {
  APP_ENV as API_ENV,
  APP_VERSION as FRONT_VERSION,
  HEADER_BG,
} from "../../../constants/env";

interface NavItemProps {
  to: string;
  icon: any;
  label: string;
  onNavigate?: () => void;
}

const NAV_ITEMS: Omit<NavItemProps, "onNavigate">[] = [
  { to: "/inicio", icon: IconHome, label: "Início" },
  { to: "/nova-consulta", icon: IconFileSearch, label: "Nova Análise" },
  { to: "/historico", icon: IconHistory, label: "Histórico" },
];

const NavItem: FC<NavItemProps> = ({ to, icon, label, onNavigate }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      style={{ width: "100%", display: "block" }}
    >
      <Flex
        align="center"
        px="10px"
        py="10px"
        borderRadius="8px"
        cursor="pointer"
        bg={isActive ? "primary.light.alpha-10" : "transparent"}
        color={isActive ? "primary.light.base" : "neutrals.text.sub-600"}
        _hover={{ bg: "primary.light.alpha-10", color: "primary.light.base" }}
        transition="all 0.15s"
        gap={3}
      >
        <Icon as={icon} boxSize="20px" />
        <Text
          fontSize="sm"
          fontWeight={isActive ? "600" : "400"}
          fontFamily="body"
          letterSpacing="-0.01em"
        >
          {label}
        </Text>
      </Flex>
    </NavLink>
  );
};

// Grupo "Relatórios" com submenu expansível (rotas /relatorios/:slug). Clicar na linha
// vai para a home de relatórios e abre o submenu; a seta (chevron) recolhe/expande.
const NavGroupRelatorios: FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const emRelatorios = location.pathname.startsWith("/relatorios");
  const [open, setOpen] = useState(emRelatorios);

  return (
    <Box>
      <Flex
        align="center"
        px="10px"
        py="10px"
        borderRadius="8px"
        cursor="pointer"
        gap={3}
        transition="all 0.15s"
        bg={location.pathname === "/relatorios" ? "primary.light.alpha-10" : "transparent"}
        color={emRelatorios ? "primary.light.base" : "neutrals.text.sub-600"}
        _hover={{ bg: "primary.light.alpha-10", color: "primary.light.base" }}
        onClick={() => {
          setOpen(true);
          navigate("/relatorios");
          onNavigate?.();
        }}
      >
        <Icon as={IconReportAnalytics} boxSize="20px" />
        <Text flex={1} fontSize="sm" fontWeight={emRelatorios ? "600" : "400"} fontFamily="body" letterSpacing="-0.01em">
          Relatórios
        </Text>
        <Icon
          as={open ? IconChevronDown : IconChevronRight}
          boxSize="16px"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        />
      </Flex>

      <Collapse in={open}>
        <VStack spacing="1px" align="stretch" pl="30px" pt="2px" pb="2px">
          {RELATORIOS.map((r) => {
            const to = `/relatorios/${r.key}`;
            const active = location.pathname === to;
            return (
              <NavLink key={r.key} to={to} onClick={onNavigate} style={{ display: "block" }}>
                <Flex
                  align="center"
                  px="10px"
                  py="7px"
                  borderRadius="8px"
                  transition="all 0.15s"
                  bg={active ? "primary.light.alpha-10" : "transparent"}
                  color={active ? "primary.light.base" : "neutrals.text.sub-600"}
                  _hover={{ bg: "primary.light.alpha-10", color: "primary.light.base" }}
                >
                  <Text fontSize="13px" fontWeight={active ? "600" : "400"} fontFamily="body">
                    {r.label}
                  </Text>
                </Flex>
              </NavLink>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};

// Grupo "Inteligência de Preços" com submenu (rotas /precos/:slug). Só admin (gate no caller).
const NavGroupPrecos: FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const emPrecos = location.pathname.startsWith("/precos");
  const [open, setOpen] = useState(emPrecos);
  return (
    <Box>
      <Flex align="center" px="10px" py="10px" borderRadius="8px" cursor="pointer" gap={3} transition="all 0.15s"
        bg={location.pathname === "/precos" ? "primary.light.alpha-10" : "transparent"}
        color={emPrecos ? "primary.light.base" : "neutrals.text.sub-600"}
        _hover={{ bg: "primary.light.alpha-10", color: "primary.light.base" }}
        onClick={() => { setOpen(true); navigate("/precos"); onNavigate?.(); }}>
        <Icon as={IconChartLine} boxSize="20px" />
        <Text flex={1} fontSize="sm" fontWeight={emPrecos ? "600" : "400"} fontFamily="body" letterSpacing="-0.01em">
          Inteligência de Preços
        </Text>
        <Icon as={open ? IconChevronDown : IconChevronRight} boxSize="16px" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} />
      </Flex>
      <Collapse in={open}>
        <VStack spacing="1px" align="stretch" pl="30px" pt="2px" pb="2px">
          {PAINEIS.map((p) => {
            const to = `/precos/${p.key}`;
            const active = location.pathname === to;
            return (
              <NavLink key={p.key} to={to} onClick={onNavigate} style={{ display: "block" }}>
                <Flex align="center" px="10px" py="7px" borderRadius="8px" transition="all 0.15s"
                  bg={active ? "primary.light.alpha-10" : "transparent"}
                  color={active ? "primary.light.base" : "neutrals.text.sub-600"}
                  _hover={{ bg: "primary.light.alpha-10", color: "primary.light.base" }}>
                  <Text fontSize="13px" fontWeight={active ? "600" : "400"} fontFamily="body">{p.label}</Text>
                </Flex>
              </NavLink>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};

const VersionFooter: FC = () => {
  const { data } = useQuery({
    queryKey: ["estudo-config"],
    queryFn: getConfig,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
  const apiVersion = data?.backVersion ? `${API_ENV} ${data.backVersion}` : "—";
  return (
    <Box px="12px" py="10px" flexShrink={0}>
      <Divider borderColor="neutrals.stroke.soft-200" mb="8px" />
      <Text fontSize="11px" color="neutrals.text.soft-400" fontFamily="body" lineHeight="1.5">
        F {FRONT_VERSION}
      </Text>
      <Text fontSize="11px" color="neutrals.text.soft-400" fontFamily="body" lineHeight="1.5">
        B {apiVersion}
      </Text>
    </Box>
  );
};

// Conteúdo do menu (marca + navegação + rodapé). Usado na sidebar fixa (desktop)
// e dentro do Drawer (mobile). `onNavigate` fecha o drawer ao clicar num item.
export const SidebarInner: FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { canRelatorios, canPrecos } = useAcesso();
  return (
  <Flex direction="column" h="100%" minH="100vh" bg="neutrals.surface.surface-0">
    <Flex bg={HEADER_BG} h="57px" px="16px" align="center" flexShrink={0}>
      {/* Nome do sistema é link para o início. */}
      <NavLink to="/inicio" style={{ display: "block" }}>
        <Text
          fontSize="sm"
          fontWeight="700"
          color="primary.light.onstate"
          fontFamily="heading"
          letterSpacing="-0.02em"
          whiteSpace="nowrap"
          cursor="pointer"
        >
          Rede Frota — Mapa de Rota
        </Text>
      </NavLink>
    </Flex>

    <Divider borderColor="neutrals.stroke.soft-200" />

    <VStack spacing={1} flex={1} px="6px" py="12px" align="stretch" overflowY="auto" overflowX="hidden">
      {NAV_ITEMS.map((item) => (
        <NavItem key={item.to} {...item} onNavigate={onNavigate} />
      ))}
      {canRelatorios && <NavGroupRelatorios onNavigate={onNavigate} />}
      {canPrecos && <NavGroupPrecos onNavigate={onNavigate} />}
    </VStack>

    <VersionFooter />
  </Flex>
  );
};

// Largura da sidebar fixa. Compartilhada com o Topbar e o conteúdo (via layout)
// para que o offset acompanhe a sidebar ao recolher/expandir.
export const SIDEBAR_WIDTH = "232px";

// Sidebar fixa (desktop). No mobile o AuthenticatedLayout a esconde e usa o Drawer.
// `isOpen` controla o recolhimento: recolhida = largura 0 (oculta), reaberta pelo
// hambúrguer da Topbar.
export const Sidebar: FC<{ isOpen?: boolean }> = ({ isOpen = true }) => (
  <Box
    w={isOpen ? SIDEBAR_WIDTH : "0px"}
    minH="100vh"
    position="fixed"
    top={0}
    left={0}
    overflow="hidden"
    transition="width 0.2s ease"
    sx={{
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        right: 0,
        width: "1px",
        height: "calc(100% - 56px)",
        backgroundColor: "var(--chakra-colors-neutrals-stroke-soft-200)",
      },
    }}
  >
    <Box w={SIDEBAR_WIDTH}>
      <SidebarInner />
    </Box>
  </Box>
);
